import { Box, Paper, Typography } from "@mui/material";
import {
  SmartForm,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { ProjectListDto } from "~/api/GeneratedApi";
import { useIsMobile } from "~/utils/hooks/useIsMobile";
import { CircularProgressWithLabel } from "./components/CircularProgressWithLabel";

type DetailsTabContentProps = {
  formData: ProjectListDto;
};

export const ProjectDetailsContent = ({ formData }: DetailsTabContentProps) => {
  const { progress } = formData;
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const gridLayout = isMobile
    ? { gridTemplateRows: "1fr auto" }
    : { gridTemplateColumns: "69% 30%" };

  return (
    <Box
      sx={{
        display: "grid",
        columnGap: "8px",
        overflow: "auto",
        ...gridLayout,
      }}
    >
      <Box>
        <Typography variant="h5"> {t("projects:details")}</Typography>
        <ProjectForm formData={formData} />
      </Box>
      <Box
        component={Paper}
        elevation={1}
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          p: 3,
        }}
      >
        <Typography variant="h5"> {t("projects:progress")}</Typography>
        <CircularProgressWithLabel value={progress} />
      </Box>
    </Box>
  );
};

type ProjectFormInputs = {
  id: number;

  name: string;
  clientName: string;
};

function ProjectForm({ formData }: DetailsTabContentProps) {
  const {
    id,

    clientName,
    name,
  } = formData;
  const { t } = useTranslation();
  const formValues: ProjectFormInputs = React.useMemo(
    () => ({
      id,
      name: name ?? "",
      clientName: clientName ?? "",
    }),
    [clientName, id, name],
  );
  const formMethods = useSmartForm<ProjectFormInputs>({
    defaultValues: formValues,
  });

  return (
    <SmartForm<ProjectFormInputs>
      gridProps={{ sx: { display: "grid", gridTemplateColumns: "1fr" } }}
      methods={formMethods}
      onSubmit={() => {}}
    >
      {Object.keys(formValues).map((k) => {
        const key = k as keyof typeof formValues;
        return (
          <SmartTextField<ProjectFormInputs>
            key={k}
            label={t(`projects:${key}`)}
            name={key}
            readOnly
            sx={{ width: "100%" }}
            inputProps={{ readOnly: true }}
          />
        );
      })}
    </SmartForm>
  );
}
